import {
  AccordionItem,
  Box,
  Center,
  Container,
  Flex,
  Grid,
  Heading,
  SystemStyleObject,
} from '@chakra-ui/react';
import {
  AccordionRoot,
  Card,
  ChevronDownIcon,
  getExpandedCardOutlineStyles,
  GradientShadowOverlay,
  Image,
  LinksWithCuttedBorders,
  pxToRem,
  SHARED_ROUTES,
} from '@cyberkongz/ui';
import { PropsWithChildren, ReactNode, useEffect, useState } from 'react';

import { BlueprintTabId } from './BlueprintLayout';
import { Subcard } from './content/cards';

export const clipPathImage =
  'polygon(100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%, 0 0)';

export const introTextStyles: SystemStyleObject = {
  maxW: '34em',
  fontSize: ['md', 'lg'],
  color: 'textSecondary',
  mt: 4,
};

export const cardsSectionBackground: SystemStyleObject = {
  backgroundPosition: ['top right', 'top center'],
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  w: '100%',
  h: '100vh',
  position: 'sticky',
  zIndex: -1,
  top: 0,
  gridRow: 1,
  gridColumn: 1,
};

function activeCardNavigationStyles(
  direction: 'left' | 'right',
  disabled: boolean
): SystemStyleObject {
  return {
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    border: '1px solid white',
    borderLeft: direction === 'left' ? '1px solid white' : 'none',
    borderRight: direction === 'right' ? '1px solid white' : 'none',
    w: pxToRem(26),
    h: pxToRem(26),
    '& svg': {
      transform: `rotateZ(${direction === 'left' ? '90deg' : '-90deg'})`,
      w: pxToRem(14),
    },
  };
}

export function IntroSectionWrapper({
  sxSection,
  stylesContainer,
  children,
}: { sxSection?: SystemStyleObject; stylesContainer?: SystemStyleObject } & PropsWithChildren) {
  return (
    <Box
      css={{
        pt: [pxToRem(440), pxToRem(320)],
        pb: [pxToRem(60), pxToRem(200)],
        backgroundImage: 'url(/static/blueprint/metaverse/intro_bg.jpg)',
        backgroundPosition: ['top 120px right', 'center'],
        backgroundSize: ['auto 380px', 'cover'],
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        ...sxSection,
      }}
    >
      <Container
        css={{
          position: 'relative',
          zIndex: 2,
          ...stylesContainer,
        }}
      >
        {children}
      </Container>
      <GradientShadowOverlay />
    </Box>
  );
}

const LINKS = [
  {
    title: 'View a full list of',
    emphasis: 'CyberKongz Partnerships',
    href: SHARED_ROUTES.PARTNERSHIPS,
  },
];

export function ListOfPartnerships() {
  return (
    <Box py={[16, 32]}>
      <Container>
        <LinksWithCuttedBorders
          links={LINKS}
          stylesContainer={{
            maxWidth: pxToRem(659),
          }}
        />
      </Container>
    </Box>
  );
}

export function useCardsSection() {
  const [activeItem, setActiveItem] = useState<string>('');

  function handleChange(value: string[]) {
    setActiveItem(value[0]);
  }

  return { activeItem, handleChange };
}

export function CardItemWrapper({ children, value }: PropsWithChildren & { value: number }) {
  return (
    <AccordionItem
      value={value.toString()}
      _open={{
        '& > *': getExpandedCardOutlineStyles(true),
      }}
    >
      <Card
        variant="landingTransparent"
        css={{
          '& a': { textDecoration: 'underline' },
        }}
      >
        {children}
      </Card>
    </AccordionItem>
  );
}

export function CardsSectionWrapper({
  activeItem,
  handleChange,
  activeCardSidebar,
  children,
  sxBackground,
  disableBackground,
}: {
  activeItem: string;
  handleChange: (value: string[]) => void;
  activeCardSidebar: ReactNode;
  sxBackground?: SystemStyleObject;
  disableBackground?: boolean;
} & PropsWithChildren) {
  return (
    <Grid
      css={{
        display: [disableBackground ? 'block' : 'grid'],
        position: 'relative',
      }}
    >
      {!disableBackground ? (
        <Box
          css={{
            backgroundImage: 'url(/static/blueprint/ecosystem/cards_bg.jpg)',
            ...cardsSectionBackground,
            ...sxBackground,
          }}
        />
      ) : null}
      <Container
        css={{ display: 'flex', position: 'relative', zIndex: 2, gridColumn: 1, gridRow: 1 }}
      >
        <AccordionRoot
          value={[activeItem]}
          defaultValue={[activeItem]}
          onValueChange={(e) => handleChange(e.value)}
          css={{
            flex: 2,
            w: '100%',
            maxWidth: ['100%', null, pxToRem(720)],
            mr: [0, pxToRem(22)],
          }}
        >
          <Grid gap={[4, 6]}>{children}</Grid>
        </AccordionRoot>
        <Box
          css={{
            display: ['none', 'block'],
            position: 'sticky',
            top: pxToRem(100),
            alignSelf: 'flex-start',
            flex: 1,
          }}
        >
          {activeCardSidebar}
        </Box>
      </Container>
      {!disableBackground ? <GradientShadowOverlay height={pxToRem(500)} /> : null}
    </Grid>
  );
}

export function ActiveCardSidebar({
  cardImage,
  subcards,
  title,
  basePath,
}: {
  title: string;
  cardImage?: string;
  subcards?: Subcard[];
  basePath: BlueprintTabId;
}) {
  const [activeSubcard, setActiveSubcard] = useState(0);
  const subcardContent = subcards ? subcards[activeSubcard] : undefined;
  const subcardContentHasTitle = subcardContent ? subcardContent.title !== undefined : false;

  useEffect(() => {
    setActiveSubcard(0);
  }, [subcards]);

  return subcards && subcardContent ? (
    <>
      <Box
        css={{
          my: [4, 0],
          borderBottom: 'thin',
          display: ['block', 'none'],
        }}
      />
      <Card
        variant="landingTransparent"
        css={{
          p: subcardContentHasTitle ? [0, 4, 8] : [0, 0],
          bg: ['transparent', 'backgroundLandingCardsTransparent'],
          backdropFilter: ['none', 'blur(15px)'],
          position: 'relative',
          '& a': { textDecoration: 'underline' },
          minHeight: ['none', subcardContentHasTitle ? pxToRem(380) : 'none'],
          display: 'flex',
          flexDirection: 'column',
          clipPath: clipPathImage,
          border: ['none', 'thin'],
          borderColor: 'neutral.700',
          '&:before': {
            position: 'absolute',
            display: ['none', 'block'],
            content: '""',
            w: pxToRem(31),
            borderTop: 'inherit',
            bottom: 0,
            right: 0,
            transform: `translate(${pxToRem(5)}, ${pxToRem(-4)}) rotateZ(135deg)`,
          },
        }}
      >
        {subcardContent.title ? (
          <Heading css={{ fontSize: 'md', lineHeight: 'base' }}>{subcardContent.title}</Heading>
        ) : null}
        {subcardContent.description ? (
          <Box css={{ mt: 2, mb: 4 }}>{subcardContent.description}</Box>
        ) : null}
        <Box
          css={{
            mt: subcardContentHasTitle ? 'auto' : 0,
            mb: [subcardContentHasTitle ? 4 : 0, null, 0],
            order: [-1, 0],
            pr: subcardContentHasTitle ? [0, null, pxToRem(80)] : 0,
          }}
        >
          <Image
            src={`/static/blueprint/${basePath}/card_${subcardContent.image}`}
            alt={subcardContent.title}
          />
        </Box>
        <Flex
          css={{
            ...(subcardContentHasTitle
              ? {
                  position: ['relative', null, 'absolute'],
                  bottom: [0, null, 8],
                  right: [0, null, 8],
                }
              : {
                  p: 4,
                  pl: [pxToRem(1), 4],
                  justifyContent: ['flex-start', 'flex-end'],
                }),
          }}
        >
          <Center
            css={activeCardNavigationStyles('left', activeSubcard === 0)}
            onClick={activeSubcard === 0 ? undefined : () => setActiveSubcard(activeSubcard - 1)}
            aria-disabled={activeSubcard === 0}
          >
            <ChevronDownIcon />
          </Center>
          <Box css={{ w: pxToRem(1), bg: 'white' }} />
          <Center
            css={activeCardNavigationStyles('right', activeSubcard === subcards.length - 1)}
            onClick={
              activeSubcard === subcards.length - 1
                ? undefined
                : () => setActiveSubcard(activeSubcard + 1)
            }
          >
            <ChevronDownIcon />
          </Center>
        </Flex>
      </Card>
    </>
  ) : (
    <Image
      src={`/static/blueprint/${basePath}/card_${cardImage}.jpg`}
      alt={title}
      css={{
        clipPath: clipPathImage,
        mt: [5, 0],
      }}
    />
  );
}
